import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Home, Gift, Crown, Mail, Loader2, Key, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { getWelcomeBack } from '@/utils/localization';

// Registracijos blokas - "Tavo raktas į NUMENAXIS namus"
export function RegistrationBlock({ userData, onRegisterSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Įveskite el. pašto adresą');
      return;
    }
    
    setLoading(true);
    try {
      // Save to localStorage for now (can be extended to backend)
      const registrationData = {
        email,
        firstName: userData?.firstName || '',
        lifePath: userData?.lifePath || 0,
        registeredAt: new Date().toISOString()
      };
      localStorage.setItem('numaxisRegistration', JSON.stringify(registrationData));
      
      setIsRegistered(true);
      toast.success('Sėkmingai užsiregistruota!');
      if (onRegisterSuccess) onRegisterSuccess(email);
    } catch (error) {
      toast.error('Klaida. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  if (isRegistered) {
    // Get gender from localStorage if available
    const storedData = localStorage.getItem('numerologyData');
    const gender = storedData ? JSON.parse(storedData).gender : null;
    
    return (
      <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30 p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Key className="w-7 h-7 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg text-green-400 mb-2">{getWelcomeBack(gender)} 🌙</h3>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Džiaugiamės, kad esi čia. NUMENAXIS – tai gyvas organizmas, kuris auga kartu su tavimi. 
              Kaip ir mažam vaikui, mums tavo kantrybė dabar yra tolygi auksui. Jei pakeliui pastebėsi 
              nesklandumų, pajausi energetinių nesutapimų ar tiesiog norėsi išreikšti padėką 
              (kurios mes labai lauksime 😊) – rašyk mums adresu{' '}
              <a href="mailto:info@numenaxis.com" className="text-amber-400 hover:underline">info@numenaxis.com</a>. 
              Mes mielai atsižvelgsime į kiekvieną tavo mintį, kad kartu sukurtume tikrai jaukią ir šviesią erdvę tavo sielai.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border-violet-500/30 p-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Home className="w-6 h-6 text-violet-400" />
        </div>
        <div>
          <h3 className="font-display text-lg text-violet-400 mb-2">
            🔑 Tavo raktas į NUMENAXIS namus
          </h3>
          <p className="text-sm text-foreground/80 leading-relaxed">
            Išsaugok savo duomenis, kad kaskart parėjus „namo" nereiktų ieškoti rakto ir visko pildyti iš naujo. 
            NUMENAXIS namai tave atpažins kaip šeimininką, o tavo duomenys saugiai lauks kitų tavo savęs pažinimo žingsnių. 
            Mes nuolat augame ir pildome šią erdvę naujomis temomis, todėl mums bus malonu tave, kaip šeimos narį, 
            pirmąjį informuoti apie mūsų plėtrą ir naujas savęs pažinimo duris.
          </p>
        </div>
      </div>
      
      <form onSubmit={handleRegister} className="flex gap-3 mt-4">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tavo@email.lt"
            className="pl-10 bg-secondary/20 border-violet-500/30"
            required
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="bg-violet-500 hover:bg-violet-600 text-white"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Susikurti namus'}
        </Button>
      </form>
    </Card>
  );
}

// VIP blokas - "Norite pilnos saldainių dėžutės?"
export function CandyBoxBlock({ onVIPClick }) {
  return (
    <Card 
      className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 p-6 cursor-pointer hover:from-amber-500/30 hover:to-orange-500/30 transition-all duration-300"
      onClick={onVIPClick}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg text-amber-400 mb-2">
            🍬 Norite pilnos saldainių dėžutės?
          </h3>
          <p className="text-sm text-foreground/80 leading-relaxed">
            Tapkite VIP nariu ir gaukite pilną savęs pažinimo PDF žemėlapį su visais gilesniaisiais "įdarais" – 
            Sielos Matricos linijų analize, savaitiniu ciklu ir partnerystės veidrodžio principu.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-amber-400 font-semibold">Tapti VIP nariu</span>
            <span className="text-amber-400">→</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// VIP blokas - "VIP narystė – tavo vidinis kompasas"
export function SoulArchiveBlock({ onVIPClick }) {
  return (
    <Card 
      className="bg-gradient-to-br from-purple-600/20 via-pink-500/20 to-amber-500/20 border-purple-500/30 p-4 sm:p-6 cursor-pointer hover:from-purple-600/30 hover:via-pink-500/30 hover:to-amber-500/30 transition-all duration-300 overflow-hidden"
      onClick={onVIPClick}
    >
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-purple-500/40">
          <BookOpen className="w-7 h-7 text-purple-400" />
        </div>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <h3 className="font-display text-lg sm:text-xl text-purple-400 mb-2">
            🧭 Atverk pilną savo vidinį žemėlapį
          </h3>
          <p className="text-sm sm:text-base text-foreground/90 leading-relaxed mb-2 break-words">
            Atrakink pilną savo Sielos žemėlapį. Tai tavo asmeninis kompasas, 
            kuris suteiks tavo kasdienybei daugiau lengvumo bei aiškumo ir padės užtikrintai 
            judėti savo tikrojo kelio link.
          </p>
          <p className="text-amber-400/90 text-xs sm:text-sm italic mb-4 break-words">
            🎁 DOVANA: Tavo asmeninė Sielos Knyga – PDF dokumentas, kuriame skaičiai atskleidžia tavo prigimtį, stiprybes ir šio gyvenimo kelio užduotis.
          </p>
          <Button className="w-full sm:w-auto bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-500 hover:via-yellow-500 hover:to-amber-600 text-black font-bold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 px-4 sm:px-6">
            <Gift className="w-5 h-5 mr-2" />
            Noriu matyti visą paveikslą ✨
          </Button>
        </div>
      </div>
    </Card>
  );
}
